// Extract `@fun:` function declarations and remove them from source text.
// Definitions are registered for later statement-call expansion.
import type { LuisCompileWarning } from '../types'
import { buildLinePreservingPadding, isValidFunctionIdentifier, resolveLineAt } from './helpers'
import type { LuisFunctionExtractionResult, LuisFunctionMap } from './types'
import { parseFunctionDefinitionAt } from './definitions-parse'

// Parse and remove all function declarations from source.
export function extractFunctionDefinitions(source: string, collectWarnings = true): LuisFunctionExtractionResult {
  const warnings: LuisCompileWarning[] = []
  const functions: LuisFunctionMap = {}
  let output = ''
  let cursor = 0

  // Start one source scan and register each valid `@fun:` declaration in order.
  while (cursor < source.length) {
    const nextFunIndex = source.indexOf('@fun:', cursor)
    // Flush remaining source when scanner finds no more declarations.
    if (nextFunIndex === -1) {
      output += source.slice(cursor)
      break
    } else {
      // Continue declaration parsing at next @fun: token.
    }

    const definitionLine = resolveLineAt(source, nextFunIndex)
    const parsed = parseFunctionDefinitionAt(source, nextFunIndex)
    // Check whether the current `@fun:` token parsed into a valid definition.
    if (!parsed) {
      // Emit syntax warning only when warning collection is enabled.
      if (collectWarnings) {
        warnings.push(createWarning(
          'invalid-function-syntax',
          definitionLine,
          'Invalid function syntax near @fun: declaration.',
        ))
      } else {
        // Continue without collecting syntax warning.
      }
      output += source.slice(cursor, nextFunIndex + 5)
      cursor = nextFunIndex + 5
      continue
    } else {
      // Continue definition registration for parsed function token.
    }

    output += source.slice(cursor, nextFunIndex)
    const definition = parsed.definition
    // Check whether the parsed definition name is valid and unique.
    if (!isValidFunctionIdentifier(definition.name)) {
      // Emit invalid-name warning only when warning collection is enabled.
      if (collectWarnings) {
        warnings.push(createWarning(
          'invalid-function-name',
          definitionLine,
          `Invalid function name "${definition.name}".`,
        ))
      } else {
        // Continue without collecting invalid-name warning.
      }
    } else if (definition.name in functions) {
      // Reject duplicate function names in the same source.
      if (collectWarnings) {
        warnings.push(createWarning(
          'duplicate-function',
          definitionLine,
          `Function "${definition.name}" is already defined.`,
        ))
      } else {
        // Continue without collecting duplicate-definition warning.
      }
    } else {
      functions[definition.name] = definition
    }

    output += buildLinePreservingPadding(source.slice(nextFunIndex, parsed.endIndex))
    cursor = parsed.endIndex
  }

  // Return stripped source, function map, and extraction warnings.
  return { source: output, functions, warnings }
}

// Build compiler warning payload for function extraction stage.
function createWarning(code: LuisCompileWarning['code'], line: number, message: string): LuisCompileWarning {
  // Return warning payload with stable shape.
  return { code, line, message }
}
